import { ApiRoutes } from "../../../../Constants/ApiRoutingConstants";
import axios, { AxiosInstance } from "axios";
import authorizeService from "../../../Identity/Utilities/AuthorizeService";
import AssetMetricsReadDto from "../../../../Models/Dashboard/AssetMetricsReadDto";
import AssetImageReadDto from "../../../../Models/Assets/AssetImageReadDto";
import SelectedAssetMetricsReadDto from "../../../../Models/Dashboard/SelectedAssetMetricsReadDto";
import { StateEventsReadDto } from "../../../../Models/State/StateEventsReadDto";
import { StateChartEventsReadDto } from "../../../../Models/State/StateChartEventsReadDto";
import LocoFuelBurnReadDto from "../../../../Models/Dashboard/LocoFuelBurnReadDto";
import LocoFlowMeterReadDto from "../../../../Models/Dashboard/LocoFlowMeterReadDto";
import LocoGpsReadDto from "../../../../Models/Dashboard/LocoGpsReadDto";

/**
 * This service provides implementations for http calls to the api pertaining to event crud functionality
 */

class AssetsDashboardApiService {
  axiosInstance: AxiosInstance;
  constructor() {
    //Subscribe to identity store the get token
    this.axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_EVENT_SERVICE_URL,
    });
  }

  async getAllAssetsMetrics(): Promise<AssetMetricsReadDto[]> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.Dashboards}/assets`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getAssetMetrics(assetId: string): Promise<SelectedAssetMetricsReadDto> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.Dashboards}/assets/${assetId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getAssetImage(imageId: string): Promise<AssetImageReadDto> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.ImagesV1}/${imageId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getTreiggeredAlerts(assetId: string): Promise<any> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.AssetsV1}/${assetId}/triggered-alerts`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getEndpointEventsState(endpointId: string, startDate: Date, endDate: Date): Promise<StateEventsReadDto[]> {
    const urlParams = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.EndpointsEventsState}/${endpointId}?${new URLSearchParams(urlParams).toString()}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getEndpointEventsStateChartData(endpointId: string, startDate: Date, endDate: Date): Promise<StateChartEventsReadDto> {
    const urlParams = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    const response = await this.axiosInstance({
      method: "GET",
      url: `${ApiRoutes.EndpointsEventsStateChartData}/${endpointId}?${new URLSearchParams(urlParams).toString()}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getLocoFuelBurn(id: string, startDate: Date, endDate: Date): Promise<LocoFuelBurnReadDto[]> {
    const urlParams = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/endpoints/events/loco-fuel-burn/${id}?${new URLSearchParams(urlParams).toString()}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getLocoFuelBurnChartData(id: string, startDate: Date, endDate: Date): Promise<any> {
    const urlParams = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/endpoints/events/loco-fuel-burn/chart-data/${id}?${new URLSearchParams(urlParams).toString()}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getLocoFlowMeter(id: string, startDate: Date, endDate: Date): Promise<LocoFlowMeterReadDto[]> {
    const urlParams = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/endpoints/events/loco-fuel-burn/flow-meter/${id}?${new URLSearchParams(urlParams).toString()}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  async getLocoFlowMeterChartData(id: string, startDate: Date, endDate: Date): Promise<any> {
    const urlParams = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/endpoints/events/loco-fuel-burn/flow-meter/chart-data/${id}?${new URLSearchParams(urlParams).toString()}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

  // Compliance cache invalidation (SuperAdmin only) — wipes the whole site's cache
  async clearSiteComplianceCache(): Promise<void> {
    await this.axiosInstance({
      method: "DELETE",
      url: `${ApiRoutes.ComplianceCache}/site`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
  }

  // Clears only the cache entries of the given asset/machine
  async clearMachineComplianceCache(machineId: string): Promise<void> {
    await this.axiosInstance({
      method: "DELETE",
      url: `${ApiRoutes.ComplianceCache}/machine/${machineId}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
  }

  async getLocoGps(id: string): Promise<LocoGpsReadDto> {
    const response = await this.axiosInstance({
      method: "GET",
      url: `web/v1/endpoints/events/loco-fuel-burn/gps/${id}`,
      headers: {
        Authorization: `Bearer ${(await authorizeService.getUser()).access_token}`,
      },
    });
    return response.data;
  }

}

const assetsDashboardApiService = new AssetsDashboardApiService();
export default assetsDashboardApiService;
